import { FormEvent, KeyboardEvent, useEffect, useState } from "react";
import { useNavigate } from "@entry/navigation";
import styled from "styled-components";

import { CheckCircleOne, Coupon } from "@assets/svg";
import { AnalyticsEvent, Routes, StorageKey } from "@ctypes";
import { useEntryIntakeStore } from "@entry/store";
import { entryColors, entryFont } from "@entry/tokens";
import { EntryCta, EntryTitle, GlassInput } from "@entry/ui";
import { useEntryAnimation } from "@entry/useEntryAnimation";
import {
  useDeeplinkPromoCode,
  useErrorBanner,
  useOnboarding,
  useRedeemPromoCode,
  useSetUserIdentity,
  useVerifyPromotionCode,
} from "@hooks";
import { Translations, i18n } from "@i18n";
import { palette } from "@layout";
import { clearDeeplinkPromoCode } from "@utils";
import { Analytics } from "@services";
import { Screen, ScreenColumn, ScreenCta, useCtaSpacer, useEntryPageAnalytics } from "./shared";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

type PromoStatus = "idle" | "success" | "error";

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  margin-top: 34px;
`;

const PromoDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  font-family: ${entryFont.body};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${entryColors.textTertiary};

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const PromoContainer = styled.div<{ $status: PromoStatus }>`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid
    ${({ $status }) =>
      $status === "error"
        ? "rgba(236, 174, 176, 0.4)"
        : $status === "success"
          ? "rgba(89, 150, 138, 0.4)"
          : entryColors.glassBorder};
  background: ${({ $status }) =>
    $status === "error"
      ? "rgba(236, 174, 176, 0.08)"
      : $status === "success"
        ? "rgba(89, 150, 138, 0.1)"
        : entryColors.glassFill};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  transition:
    background 180ms ease,
    border-color 180ms ease;
`;

const PromoInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  margin: 0;
  padding: 0;
  font-family: ${entryFont.body};
  font-size: 14px;
  letter-spacing: -0.28px;
  color: ${entryColors.textPrimary};
  caret-color: ${entryColors.highlight};

  &::placeholder {
    color: ${entryColors.textTertiary};
  }
`;

const PromoApplyButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 4px 0;
  font-family: ${entryFont.body};
  font-size: 12px;
  font-weight: 400;
  color: ${entryColors.highlight};
  cursor: pointer;

  &:disabled {
    color: ${entryColors.textTertiary};
    cursor: default;
  }
`;

const PromoCodeText = styled.span`
  flex: 1;
  font-family: ${entryFont.body};
  font-size: 14px;
  letter-spacing: -0.28px;
  color: ${entryColors.textPrimary};
  text-transform: uppercase;
`;

const PromoAppliedRow = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const PromoAppliedText = styled.span`
  font-family: ${entryFont.body};
  font-size: 12px;
  color: ${palette.teal[100]};
`;

const PromoMessage = styled.p<{ $status: PromoStatus }>`
  margin: -12px 2px 0;
  font-family: ${entryFont.body};
  font-size: 13px;
  color: ${({ $status }) => ($status === "success" ? palette.teal[100] : entryColors.error)};
`;


export const EmailScreen = () => {
  const navigate = useNavigate();
  const { intake, setIntake } = useEntryIntakeStore();

  const deeplinkPromoCode = useDeeplinkPromoCode();

  const [email, setEmail] = useState(intake.email);
  const [promoCode, setPromoCode] = useState(intake.promotionCode || deeplinkPromoCode || "");
  const [promoStatus, setPromoStatus] = useState<PromoStatus>(
    intake.promotionCode ? "success" : "idle",
  );
  // The API's own wording for what the code is worth ("Successfully redeemed $50"). Preferred over
  // the generic success line so the user reads back the value the card promised them.
  const [redemptionText, setRedemptionText] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const [verifyPromotionCode, { errorMessage, loading: promoLoading }] = useVerifyPromotionCode();

  // Phone-first signup lands here already authenticated: the account exists, so the email is an
  // update rather than something registerWithPhone will carry, and this is the last intake step
  // before the rest of onboarding.
  const isAuthenticated = Boolean(localStorage.getItem(StorageKey.Token));

  const [setUserIdentity, { loading: identityLoading }] = useSetUserIdentity();
  const [redeemPromoCode] = useRedeemPromoCode();
  const { resetToNext } = useOnboarding();
  const { showErrorBanner } = useErrorBanner();

  useEntryPageAnalytics(Routes.EntryEmail);

  const { contentStyle, formStyle, ctaStyle, animateOut } = useEntryAnimation();
  const { ctaRef, ctaSpacerHeight } = useCtaSpacer();

  const trimmed = email.trim();
  const valid = EMAIL_PATTERN.test(trimmed);
  const error = touched && trimmed && !valid ? i18n.t(Translations.EntryEmailInvalid) : undefined;

  const verifyCode = async (code: string) => {
    const trimmedCode = code.trim();
    if (!trimmedCode) return;

    setRedemptionText(null);

    try {
      const { data } = await verifyPromotionCode({
        variables: { code: trimmedCode },
      });

      if (data?.verifyPromotionCode?.active) {
        Analytics.track(AnalyticsEvent.PromoCodeApplied, { code: trimmedCode });
        setIntake({ promotionCode: trimmedCode });
        setRedemptionText(data.verifyPromotionCode.redemptionText ?? null);
        setPromoStatus("success");
        return;
      }

      Analytics.track(AnalyticsEvent.PromoCodeError, { code: trimmedCode });
      setPromoStatus("error");
    } catch {
      Analytics.track(AnalyticsEvent.PromoCodeError, { code: trimmedCode });
      setPromoStatus("error");
    }
  };

  /* eslint-disable react-hooks/set-state-in-effect -- async verification on deeplink arrival */
  useEffect(() => {
    if (deeplinkPromoCode && !intake.promotionCode) {
      void verifyCode(deeplinkPromoCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deeplinkPromoCode]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleChangePromo = (value: string) => {
    setPromoCode(value);

    if (promoStatus !== "idle") {
      setPromoStatus("idle");
      setRedemptionText(null);
      setIntake({ promotionCode: "" });
    }
  };

  // Enter inside the promo field applies the code (mobile's
  // onSubmitEditing) instead of submitting the whole form.
  const handlePromoKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void verifyCode(promoCode);
    }
  };

  const promoMessage = (() => {
    if (promoStatus === "success") {
      return redemptionText || i18n.t(Translations.SignUpRouteReferralCodeSuccessMessage);
    }

    if (promoStatus === "error") {
      return errorMessage || i18n.t(Translations.SignUpRouteReferralCodeErrorMessage);
    }

    return null;
  })();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid || identityLoading) return;

    Analytics.track(AnalyticsEvent.EntryEmailPrimaryCTAPressed);
    setIntake({
      email: trimmed,
      promotionCode: promoCode.trim(),
    });

    try {
      // Same key the existing sign-up flow persists, so the password step
      // and the "I have an account → Email" heuristic both see it.
      localStorage.setItem(StorageKey.Email, trimmed);
    } catch {
      // Storage unavailable – the intake store still carries the email.
    }

    if (isAuthenticated) {
      try {
        await setUserIdentity({ variables: { email: trimmed } });

        const code = promoCode.trim();

        // registerWithPhone redeems the code on the get-started journey. There is no registration
        // left on this path, so redeem it here or it is silently dropped. Non-fatal: a failed
        // redemption must not strand the user on the last intake screen.
        if (code && promoStatus === "success") {
          await redeemPromoCode({ variables: { code } })
            .then(() => clearDeeplinkPromoCode())
            .catch(() => undefined);
        }

        animateOut(() => resetToNext());
      } catch {
        showErrorBanner();
      }
      return;
    }

    // Passwordless: account is created via phone number + SMS code,
    // mirroring mobile. The email is attached at registration.
    animateOut(() => navigate(Routes.EntryPhone, { state: { signup: true } }));
  };

  return (
    <Screen>
      <ScreenColumn as="form" onSubmit={handleSubmit} noValidate>
        <div style={{ ...contentStyle, marginTop: 0 }}>
          <EntryTitle $align="center" $size={38}>
            {i18n.t(Translations.EntryEmailTitle)}
          </EntryTitle>
        </div>

        <div style={formStyle}>
          <Fields>
            <GlassInput
              id="entry-email"
              type="email"
              name="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              label={i18n.t(Translations.EntryEmailLabel)}
              placeholder={i18n.t(Translations.EntryEmailPlaceholder)}
              value={email}
              autoFocus
              error={error}
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => setTouched(true)}
            />

            <PromoDivider>{i18n.t(Translations.EntryPromoDivider)}</PromoDivider>

            {promoStatus === "success" ? (
              <PromoContainer $status="success">
                <Coupon fill={palette.white[60]} />
                <PromoCodeText>{promoCode}</PromoCodeText>
                <PromoAppliedRow>
                  <PromoAppliedText>{i18n.t(Translations.EntryPromoApplied)}</PromoAppliedText>
                  <CheckCircleOne fill={palette.teal[100]} size={15} />
                </PromoAppliedRow>
              </PromoContainer>
            ) : (
              <PromoContainer $status={promoStatus}>
                <Coupon fill={palette.white[60]} />
                <PromoInput
                  id="entry-promo"
                  name="promo-code"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  placeholder={i18n.t(Translations.EntryPromoPlaceholder)}
                  aria-label={i18n.t(Translations.EntryPromoPlaceholder)}
                  value={promoCode}
                  onChange={(event) => handleChangePromo(event.target.value)}
                  onKeyDown={handlePromoKeyDown}
                />
                <PromoApplyButton
                  type="button"
                  onClick={() => void verifyCode(promoCode)}
                  disabled={promoLoading || !promoCode.trim()}
                >
                  {i18n.t(Translations.EntryPromoApply)}
                </PromoApplyButton>
              </PromoContainer>
            )}

            {promoMessage ? (
              <PromoMessage $status={promoStatus}>{promoMessage}</PromoMessage>
            ) : null}
          </Fields>
        </div>

        <div aria-hidden="true" style={{ height: ctaSpacerHeight, flexShrink: 0 }} />
        <ScreenCta ref={ctaRef} style={ctaStyle}>
          <EntryCta
            type="submit"
            $variant="green"
            $disabled={!valid || identityLoading}
            disabled={!valid || identityLoading}
            aria-disabled={!valid || identityLoading}
          >
            {i18n.t(Translations.EntryEmailPrimaryCTA)}
          </EntryCta>
        </ScreenCta>
      </ScreenColumn>
    </Screen>
  );
};
